import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2, Plus, Check } from "lucide-react";
import { toast } from "sonner";

import { useHoldings } from "@/admin/hooks/use-products";
import {
  useCheckClaimDuplicates,
  useCreateChildClaim,
  useCreateClaimMetadata,
  type PotentialDuplicateClaim,
} from "@/admin/hooks/use-claims";
import { PotentialDuplicateClaimDialog } from "@/admin/components/claims/potential-duplicate-claim-dialog";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SearchInput } from "@/shared/components/ui/search-input";
import { Label } from "@/shared/components/ui/label";
import { ImageUpload } from "@/admin/components/image-upload";
import {
  MetadataField,
  normaliseClaimField,
  resolveDependentOptions,
  isFieldVisible,
  isConditionallyRequired,
  isRequiredValueProvided,
  FILE_FIELD_TYPES,
} from "@/shared/lib/forms";
import { toastApiError } from "@/shared/lib/api-error";

/**
 * Adds a single unit-level child claim under an HOA parent claim.
 *
 * Two phases inside one dialog:
 *   1. Pick the unit's registration (holding) — searchable list.
 *   2. Fill the installed-claim details (fields from create-claim metadata),
 *      then submit via POST /claims/claims/<parent>/create-child/.
 *
 * The child claim is always an "installed" claim (it references a real
 * holding); the parent provides the HOA grouping.
 */
export function CreateChildClaimDialog({
  open,
  onOpenChange,
  parentUuid,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  parentUuid: string;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [holdingUuid, setHoldingUuid] = useState<string | null>(null);
  const [holdingDisplay, setHoldingDisplay] = useState<string | null>(null);
  const [values, setValues] = useState<Record<string, unknown>>({});

  // WPS-150: create-time potential-duplicate pre-check. Runs before the child
  // claim is filed; a match opens the shared warning pop-up. "Continue" re-runs
  // submit with the gate skipped (via an explicit arg, so no stale-state race).
  const [duplicateOpen, setDuplicateOpen] = useState(false);
  const [duplicateMatches, setDuplicateMatches] = useState<
    PotentialDuplicateClaim[]
  >([]);

  const createChild = useCreateChildClaim();
  const checkDuplicates = useCheckClaimDuplicates();

  const params = useMemo(() => {
    const p: Record<string, string> = { page: "1", page_size: "8" };
    if (search) p.search = search;
    return p;
  }, [search]);

  const { data: holdingsData, isLoading: holdingsLoading } = useHoldings(
    holdingUuid ? undefined : params,
  );
  const holdings = (holdingsData?.results ?? []) as Array<{
    uuid: string;
    product?: { cached_long_name?: string; cached_short_name?: string };
    host?: { address_line_1?: string; address_city?: string };
  }>;

  const { data: metadata, isLoading: metadataLoading } = useCreateClaimMetadata(
    "installed",
    { holdingUuid },
  );
  const fields = metadata?.fields ?? [];

  const visibleFields = fields.filter((f) =>
    isFieldVisible(f.configuration_data, values),
  );
  const fileFields = visibleFields.filter((f) =>
    FILE_FIELD_TYPES.has(f.field_type),
  );
  const inputFields = visibleFields.filter(
    (f) => !FILE_FIELD_TYPES.has(f.field_type),
  );

  const reset = () => {
    setSearch("");
    setHoldingUuid(null);
    setHoldingDisplay(null);
    setValues({});
    setDuplicateOpen(false);
    setDuplicateMatches([]);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleChange = (codeName: string, value: unknown) => {
    setValues((prev) => ({ ...prev, [codeName]: value }));
  };

  const handleSelectHolding = (h: (typeof holdings)[number]) => {
    setHoldingUuid(h.uuid);
    const productName =
      h.product?.cached_long_name || h.product?.cached_short_name || "Product";
    const addr = [h.host?.address_line_1, h.host?.address_city]
      .filter(Boolean)
      .join(", ");
    setHoldingDisplay(addr ? `${productName} — ${addr}` : productName);
  };

  const missingRequired = visibleFields.filter((f) => {
    const required =
      f.required || isConditionallyRequired(f.configuration_data, values);
    // `isRequiredValueProvided` treats a `false` acknowledgement checkbox as
    // unanswered (check-then-uncheck), unlike a plain emptiness test (DL-44).
    return required && !isRequiredValueProvided(f, values[f.code_name]);
  });

  const handleSubmit = async (opts?: { skipDuplicateCheck?: boolean }) => {
    if (!holdingUuid) return;
    if (missingRequired.length > 0) {
      toast.error(
        `Please complete: ${missingRequired.map((f) => f.display_name).join(", ")}`,
      );
      return;
    }
    // WPS-150: warn on potential duplicates before filing. Best-effort - a
    // failed pre-check never blocks the claim. "Continue" from the pop-up
    // re-enters here with the gate skipped.
    if (!opts?.skipDuplicateCheck) {
      try {
        const matches = await checkDuplicates.mutateAsync({
          holdingUuid,
          claimSchemaKey: "installed",
        });
        if (matches.length > 0) {
          setDuplicateMatches(matches);
          setDuplicateOpen(true);
          return;
        }
      } catch {
        // Ignore - proceed to file the claim.
      }
    }
    try {
      await createChild.mutateAsync({
        parentUuid,
        holding_uuid: holdingUuid,
        claim_schema_key: "installed",
        attribute_values: values,
      });
      toast.success("Unit claim added");
      handleClose(false);
    } catch (err: unknown) {
      toastApiError(err, "Failed to add unit claim");
    }
  };

  const handleContinueDespiteDuplicates = () => {
    setDuplicateOpen(false);
    void handleSubmit({ skipDuplicateCheck: true });
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[640px]">
        <DialogHeader>
          <DialogTitle className="text-[15px] font-normal text-[var(--ink)]">
            Add Unit Claim
          </DialogTitle>
          <DialogDescription className="text-[12px] text-[var(--ink-3)]">
            {holdingUuid
              ? "Fill in the defect details for this unit."
              : "Pick the unit's registration to file a claim against."}
          </DialogDescription>
        </DialogHeader>

        {/* Phase 1 — holding picker */}
        {!holdingUuid && (
          <div className="mt-2 flex flex-col rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)]">
            <div className="border-b-[0.5px] border-[var(--rule)] px-4 py-2.5">
              <SearchInput
                placeholder="Search by address, owner, product, or Reg #..."
                value={search}
                onChange={setSearch}
                className="h-8 text-[13px]"
              />
            </div>
            <div className="clara-scroll max-h-[360px] overflow-y-auto">
              {holdingsLoading ? (
                <p className="px-4 py-5 text-[13px] text-[var(--ink-3)]">
                  Loading registrations...
                </p>
              ) : holdings.length === 0 ? (
                <p className="px-4 py-8 text-center text-[13px] text-[var(--ink-3)]">
                  No registrations match your search
                </p>
              ) : (
                holdings.map((h) => {
                  const productName =
                    h.product?.cached_long_name ||
                    h.product?.cached_short_name ||
                    "Product";
                  const addr = [h.host?.address_line_1, h.host?.address_city]
                    .filter(Boolean)
                    .join(", ");
                  return (
                    <Button
                      key={h.uuid}
                      variant="ghost"
                      onClick={() => handleSelectHolding(h)}
                      className="flex h-auto w-full cursor-pointer flex-col items-start gap-0.5 rounded-none border-b-[0.5px] border-[var(--rule)] px-4 py-3 text-left font-normal transition-colors last:border-b-0 hover:bg-[var(--bg)]"
                    >
                      <span className="text-[13px] text-[var(--ink)]">
                        {productName}
                      </span>
                      {addr && (
                        <span className="text-[11px] text-[var(--ink-3)]">
                          {addr}
                        </span>
                      )}
                    </Button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Phase 2 — details form */}
        {holdingUuid && (
          <div className="mt-2 flex flex-col gap-4">
            <div className="flex items-center justify-between rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
              <span className="flex items-center gap-2 text-[12px] text-[var(--ink-2)]">
                <Check size={13} className="text-[var(--green)]" />
                {holdingDisplay}
              </span>
              <Button
                variant="ghost"
                onClick={() => {
                  setHoldingUuid(null);
                  setHoldingDisplay(null);
                  setValues({});
                }}
                className="h-auto cursor-pointer p-0 text-[11px] font-normal uppercase tracking-[0.08em] text-[var(--green)] hover:text-[var(--ink)]"
              >
                Change
              </Button>
            </div>

            {metadataLoading ? (
              <p className="py-4 text-[13px] text-[var(--ink-3)]">
                Loading claim fields...
              </p>
            ) : (
              <div className="clara-scroll flex max-h-[420px] flex-col gap-4 overflow-y-auto pr-1">
                {inputFields.map((field) => {
                  const normalised = normaliseClaimField(field);
                  const depOptions = resolveDependentOptions(
                    field.configuration_data,
                    values,
                  );
                  if (depOptions) normalised.options = depOptions;
                  return (
                    <MetadataField
                      key={field.code_name}
                      field={normalised}
                      value={values[field.code_name]}
                      onChange={(v) => handleChange(field.code_name, v)}
                      labelVariant="compact"
                    />
                  );
                })}
                {fileFields.map((field) => (
                  <div key={field.code_name}>
                    <Label className="mb-1.5 block gap-0 text-[10px] uppercase tracking-[0.14em] text-[var(--ink-3)]">
                      {field.display_name}
                    </Label>
                    <ImageUpload
                      value={
                        typeof values[field.code_name] === "string"
                          ? (values[field.code_name] as string)
                          : null
                      }
                      onChange={(uuid) => handleChange(field.code_name, uuid)}
                      useCase="CLAIM_EVIDENCE"
                      label=""
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleClose(false)}
            className="cursor-pointer"
          >
            Cancel
          </Button>
          {holdingUuid && (
            <Button
              onClick={() => handleSubmit()}
              disabled={createChild.isPending || checkDuplicates.isPending}
              className="cursor-pointer"
            >
              {createChild.isPending || checkDuplicates.isPending ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <Plus className="h-3.5 w-3.5" />
              )}
              Add Unit Claim
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
      <PotentialDuplicateClaimDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        matches={duplicateMatches}
        onContinue={handleContinueDespiteDuplicates}
        isContinuing={createChild.isPending}
        continueLabel={t(
          "claims.duplicate_warning.add_unit_claim_anyway",
          "Add unit claim anyway",
        )}
      />
    </Dialog>
  );
}
